<template>
  <div class="p-6">
    <!-- Phone number selector -->
    <div class="flex items-center gap-4 mb-4">
      <select
        v-model="selectedPhoneId"
        class="text-sm border border-gray-300 rounded-md px-3 py-1.5"
        :disabled="phonesLoading"
        @change="handlePhoneChange"
      >
        <option :value="null" disabled>Select a phone number</option>
        <option v-for="phone in phoneNumbers" :key="phone.id" :value="phone.id">
          {{ phone.assigned_user_name || 'Unassigned' }} — {{ formatPhone(phone.phone_number) }}
        </option>
      </select>
      <select
        v-model="filters.status"
        class="text-sm border border-gray-300 rounded-md px-3 py-1.5"
        :disabled="!selectedPhoneId"
        @change="resetAndFetch"
      >
        <option value="">All</option>
        <option value="completed">Completed</option>
        <option value="busy">Busy</option>
        <option value="no-answer">No Answer</option>
        <option value="failed">Failed</option>
        <option value="canceled">Canceled</option>
      </select>
      <button
        v-if="selectedPhoneId"
        class="ml-auto text-xs text-gray-500 hover:text-gray-700"
        :disabled="loading"
        @click="fetchCallLogs"
      >
        <font-awesome-icon :icon="['fas', 'arrows-rotate']" class="mr-1" />
        Refresh
      </button>
    </div>

    <!-- No phone selected -->
    <div v-if="!selectedPhoneId && !phonesLoading" class="py-12 text-center">
      <font-awesome-icon :icon="['fas', 'phone']" class="text-3xl text-gray-300 mb-3" />
      <p class="text-sm text-gray-500">Select a phone number to view call logs</p>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="py-12 text-center">
      <font-awesome-icon :icon="['fas', 'circle-notch']" class="text-xl text-gray-400 animate-spin" />
    </div>

    <!-- Empty -->
    <div v-else-if="selectedPhoneId && callLogs.length === 0" class="py-12 text-center">
      <font-awesome-icon :icon="['fas', 'phone-slash']" class="text-3xl text-gray-300 mb-3" />
      <p class="text-sm text-gray-500">No call logs found</p>
    </div>

    <!-- Table -->
    <template v-else-if="callLogs.length > 0">
      <table class="min-w-full divide-y divide-gray-200">
        <thead>
          <tr>
            <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Direction</th>
            <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">From</th>
            <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">To</th>
            <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
            <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Duration</th>
            <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date/Time</th>
            <th class="w-10 px-4 py-3"></th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-200">
          <template v-for="log in callLogs" :key="log.uuid">
            <tr
              class="hover:bg-gray-50 cursor-pointer"
              @click="toggleExpand(log)"
            >
              <td class="px-4 py-3 text-sm text-gray-600">
                <font-awesome-icon
                  :icon="['fas', log.direction === 'inbound' ? 'phone-arrow-down-left' : 'phone-arrow-up-right']"
                  class="mr-1"
                />
                {{ log.direction }}
              </td>
              <td class="px-4 py-3 text-sm text-gray-600 tabular-nums">{{ formatPhone(log.from_number) }}</td>
              <td class="px-4 py-3 text-sm text-gray-600 tabular-nums">{{ formatPhone(log.to_number) }}</td>
              <td class="px-4 py-3">
                <span
                  class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                  :class="statusClass(log.status)"
                >
                  {{ log.status }}
                </span>
              </td>
              <td class="px-4 py-3 text-sm text-gray-600 tabular-nums">{{ formatDuration(log.duration_seconds) }}</td>
              <td class="px-4 py-3 text-sm text-gray-500">{{ formatTimestamp(log.created_at) }}</td>
              <td class="px-4 py-3 text-center">
                <font-awesome-icon
                  v-if="log.has_recordings"
                  :icon="['fas', 'volume-high']"
                  :class="expandedUuid === log.uuid ? 'text-primary-500' : 'text-gray-400'"
                />
              </td>
            </tr>
            <tr v-if="expandedUuid === log.uuid">
              <td colspan="7" class="px-4 py-4 bg-gray-50">
                <div v-if="expandedDetail === null" class="text-sm text-gray-400">
                  <font-awesome-icon :icon="['fas', 'circle-notch']" class="animate-spin mr-1" />
                  Loading recordings...
                </div>
                <div v-else-if="expandedDetail.recordings && expandedDetail.recordings.length > 0">
                  <p class="text-xs font-medium text-gray-500 uppercase mb-2">Recordings</p>
                  <div
                    v-for="(rec, i) in expandedDetail.recordings"
                    :key="i"
                    class="flex items-center gap-3 mb-2"
                  >
                    <span class="text-xs text-gray-500">{{ rec.recording_type || 'recording' }}</span>
                    <span class="text-xs text-gray-400 tabular-nums">{{ formatDuration(rec.duration_seconds) }}</span>
                    <template v-if="rec._blobUrl">
                      <audio :src="rec._blobUrl" controls class="h-8 w-48" />
                    </template>
                    <template v-else>
                      <button
                        class="text-xs text-primary-500 hover:text-primary-600 font-medium"
                        :disabled="rec._loading"
                        @click.stop="loadRecordingAudio(rec)"
                      >
                        <font-awesome-icon
                          :icon="['fas', rec._loading ? 'circle-notch' : 'play']"
                          :class="{ 'animate-spin': rec._loading }"
                          class="mr-1"
                        />
                        {{ rec._loading ? 'Loading...' : 'Play' }}
                      </button>
                    </template>
                  </div>
                </div>
                <div v-else class="text-sm text-gray-400">No recordings</div>
              </td>
            </tr>
          </template>
        </tbody>
      </table>

      <!-- Pagination -->
      <div class="flex items-center justify-between px-4 py-3 border-t border-gray-200">
        <div class="flex items-center gap-3">
          <p class="text-sm text-gray-500">
            Showing {{ offset + 1 }}–{{ offset + callLogs.length }} of {{ totalCount }}
          </p>
          <select
            v-model.number="limit"
            class="text-xs border border-gray-300 rounded px-2 py-1"
            @change="handleLimitChange"
          >
            <option :value="10">10 / page</option>
            <option :value="20">20 / page</option>
            <option :value="50">50 / page</option>
            <option :value="100">100 / page</option>
          </select>
        </div>
        <div class="flex gap-2">
          <button
            class="btn-secondary text-xs"
            :disabled="offset === 0"
            @click="prevPage"
          >
            Previous
          </button>
          <button
            class="btn-secondary text-xs"
            :disabled="offset + limit >= totalCount"
            @click="nextPage"
          >
            Next
          </button>
        </div>
      </div>
    </template>

    <div v-if="error" class="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
      {{ error }}
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { listTenantPhoneNumbers, listPhoneNumberCallLogs } from '@/services/api/tenantPhoneNumbers'
import { getCallLog } from '@/services/api/voiceApi'

export default {
  name: 'TelephonyCallLogs_Admin',
  components: { FontAwesomeIcon },

  data() {
    return {
      phoneNumbers: [],
      phonesLoading: true,
      selectedPhoneId: null,
      callLogs: [],
      totalCount: 0,
      loading: false,
      error: null,
      offset: 0,
      limit: 20,
      filters: {
        status: '',
      },
      expandedUuid: null,
      expandedDetail: null,
    }
  },

  mounted() {
    this.fetchPhoneNumbers()
  },

  beforeUnmount() {
    if (this.expandedDetail?.recordings) {
      this.expandedDetail.recordings.forEach((rec) => {
        if (rec._blobUrl) URL.revokeObjectURL(rec._blobUrl)
      })
    }
  },

  methods: {
    async fetchPhoneNumbers() {
      this.phonesLoading = true
      try {
        this.phoneNumbers = await listTenantPhoneNumbers()
      } catch {
        this.error = 'Failed to load phone numbers'
      } finally {
        this.phonesLoading = false
      }
    },

    handlePhoneChange() {
      this.offset = 0
      this.filters.status = ''
      this.fetchCallLogs()
    },

    resetAndFetch() {
      this.offset = 0
      this.fetchCallLogs()
    },

    async fetchCallLogs() {
      if (!this.selectedPhoneId) return
      this.loading = true
      this.error = null
      this.expandedUuid = null
      this.expandedDetail = null
      try {
        const params = { limit: this.limit, offset: this.offset }
        if (this.filters.status) params.status = this.filters.status
        const data = await listPhoneNumberCallLogs(this.selectedPhoneId, params)
        this.callLogs = data.results
        this.totalCount = data.count
      } catch {
        this.error = 'Failed to load call logs'
      } finally {
        this.loading = false
      }
    },

    async toggleExpand(log) {
      if (this.expandedUuid === log.uuid) {
        this.expandedUuid = null
        this.expandedDetail = null
        return
      }
      if (this.expandedDetail?.recordings) {
        this.expandedDetail.recordings.forEach((rec) => {
          if (rec._blobUrl) URL.revokeObjectURL(rec._blobUrl)
        })
      }
      this.expandedUuid = log.uuid
      this.expandedDetail = null
      try {
        const detail = await getCallLog(log.uuid)
        if (detail.recordings) {
          detail.recordings = detail.recordings.map((rec) => ({
            ...rec,
            _blobUrl: null,
            _loading: false,
          }))
        }
        this.expandedDetail = detail
      } catch {
        this.expandedDetail = { recordings: [] }
      }
    },

    async loadRecordingAudio(rec) {
      if (!rec.audio_url) return
      rec._loading = true
      try {
        const resp = await axios.get(rec.audio_url, { responseType: 'blob' })
        rec._blobUrl = URL.createObjectURL(resp.data)
      } catch {
        this.error = 'Failed to load audio'
      } finally {
        rec._loading = false
      }
    },

    prevPage() {
      this.offset = Math.max(0, this.offset - this.limit)
      this.fetchCallLogs()
    },

    nextPage() {
      this.offset += this.limit
      this.fetchCallLogs()
    },

    handleLimitChange() {
      this.offset = 0
      this.fetchCallLogs()
    },

    statusClass(status) {
      const map = {
        completed: 'bg-green-100 text-green-800',
        busy: 'bg-yellow-100 text-yellow-800',
        'no-answer': 'bg-yellow-100 text-yellow-800',
        failed: 'bg-red-100 text-red-800',
        canceled: 'bg-gray-100 text-gray-600',
      }
      return map[status] || 'bg-gray-100 text-gray-600'
    },

    formatPhone(phone) {
      if (!phone) return '—'
      const digits = phone.replace(/\D/g, '')
      if (digits.length === 11 && digits[0] === '1') {
        return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`
      }
      if (digits.length === 10) {
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
      }
      return phone
    },

    formatDuration(seconds) {
      if (!seconds) return '—'
      const m = Math.floor(seconds / 60)
      const s = seconds % 60
      return `${m}:${String(s).padStart(2, '0')}`
    },

    formatTimestamp(ts) {
      if (!ts) return ''
      const d = new Date(ts)
      return (
        d.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' }) +
        ', ' +
        d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
      )
    },
  },
}
</script>
